import searchIcon from "@loot-os/icons/icons/search";
import slidersIcon from "@loot-os/icons/icons/sliders";
import { useState } from "react";
import { Button } from "../../../../core/Button.js";
import { Checkbox } from "../../../../core/Checkbox.js";
import { Input } from "../../../../core/Input.js";
import { Select } from "../../../../core/Select.js";
import { Sheet, SheetClose, SheetFooter } from "../../../../core/Sheet.js";
import { useLayout } from "../../../../foundations/themeContext.js";
import { useMediaQuery } from "../../../../foundations/useMediaQuery.js";
import type { BoxFiltersContent, BoxSort } from "../../boxesContent.js";

/**
 * Search, a price range, the sort, the balance filter and Clear, each named: a row from md, and on
 * a phone a sheet behind a floating button that counts the filters set.
 */
export function BoxesFilters(content: BoxFiltersContent) {
  return (
    <>
      <FilterRow {...content} />
      <FilterSheet {...content} />
    </>
  );
}

function FilterRow({ search, price, sort, affordable, clear, activeCount }: BoxFiltersContent) {
  return (
    <div className="flex flex-wrap items-center gap-3 max-md:hidden">
      <Input
        ref={search.focusRef}
        type="search"
        label={search.label}
        hideLabel
        icon={searchIcon}
        placeholder={search.placeholder}
        value={search.value}
        onChange={(event) => search.onChange(event.target.value)}
        className="min-w-56 flex-1"
      />
      {[price.min, price.max].map((field) => (
        <Input
          key={field.label}
          label={field.label}
          hideLabel
          mono
          inputMode="decimal"
          placeholder={field.placeholder}
          value={field.value}
          invalid={field.invalid}
          onChange={(event) => field.onChange(event.target.value)}
          className="w-32"
        />
      ))}
      <Select
        label={sort.label}
        hideLabel
        options={sort.options}
        value={sort.value}
        onValueChange={(next: BoxSort) => sort.onValueChange(next)}
        className="min-w-44"
      />
      {affordable && (
        <Checkbox checked={affordable.checked} onCheckedChange={affordable.onCheckedChange}>
          {affordable.label}
        </Checkbox>
      )}
      {activeCount > 0 && (
        <Button variant="ghost" size="sm" onClick={clear.onClear}>
          {clear.label}
        </Button>
      )}
    </div>
  );
}

/** The same filters stacked in a bottom sheet; they apply as they change, so Done only closes. */
function FilterSheet({
  search,
  price,
  sort,
  affordable,
  clear,
  activeCount,
  sheet,
}: BoxFiltersContent) {
  const { chrome } = useLayout();
  const [open, setOpen] = useState(false);
  // From md the row stands in for the sheet, so a sheet left open as the screen widens (a phone
  // turned sideways) closes.
  const wide = useMediaQuery("(min-width: 768px)");
  const [wasWide, setWasWide] = useState(wide);
  if (wide !== wasWide) {
    setWasWide(wide);
    if (wide) setOpen(false);
  }
  return (
    <Sheet
      side="bottom"
      title={sheet.title}
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          variant={chrome === "glass" ? "glass" : "secondary"}
          icon={slidersIcon}
          aria-label={sheet.countLabel(activeCount)}
          data-floating-action
          trailing={
            activeCount > 0 && (
              // The space keeps the label and the count apart in the button's text, "Filters 2",
              // which its name then holds in order.
              <>
                {" "}
                <span
                  aria-hidden
                  className="rounded-pill bg-accent-default px-1.5 py-0.5 font-mono text-mono-meta leading-none text-text-on-accent tabular-nums"
                >
                  {activeCount}
                </span>
              </>
            )
          }
          className="fixed inset-x-0 bottom-[calc(var(--bottom-bar-inset,0px)+0.75rem)] z-30 mx-auto w-fit rounded-pill shadow-float md:hidden"
        >
          {sheet.label}
        </Button>
      }
      footer={
        <SheetFooter className="flex-row items-center justify-between sm:justify-between">
          <Button variant="ghost" disabled={activeCount === 0} onClick={clear.onClear}>
            {clear.label}
          </Button>
          <SheetClose render={<Button>{sheet.doneLabel}</Button>} />
        </SheetFooter>
      }
    >
      <Input
        type="search"
        label={search.label}
        hideLabel
        icon={searchIcon}
        placeholder={search.placeholder}
        value={search.value}
        onChange={(event) => search.onChange(event.target.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        {[price.min, price.max].map((field) => (
          <Input
            key={field.label}
            label={field.label}
            mono
            inputMode="decimal"
            placeholder={field.placeholder}
            value={field.value}
            invalid={field.invalid}
            onChange={(event) => field.onChange(event.target.value)}
          />
        ))}
      </div>
      <Select
        label={sort.label}
        options={sort.options}
        value={sort.value}
        onValueChange={(next: BoxSort) => sort.onValueChange(next)}
      />
      {affordable && (
        <Checkbox checked={affordable.checked} onCheckedChange={affordable.onCheckedChange}>
          {affordable.label}
        </Checkbox>
      )}
    </Sheet>
  );
}
